import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, Loader2, MessageCircle, Sparkles, Heart, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useAuth } from '../AuthContext';
import { useToast } from '../hooks/use-toast';

const Companion = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const messagesEndRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companion/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        
        // Load the most recent conversation if exists
        if (data.conversations && data.conversations.length > 0) {
          loadConversation(data.conversations[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };

  const loadConversation = async (conversationId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companion/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentConversation(conversationId);
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || isLoading) return;

    const userMessage = newMessage.trim();
    setNewMessage('');
    setIsLoading(true);
    setIsTyping(true);

    // Add user message to UI immediately
    const tempUserMessage = {
      id: Date.now(),
      sender: 'user',
      message: userMessage,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const response = await fetch(`${API_BASE_URL}/api/companion/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_id: currentConversation,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Update current conversation ID if new
        if (!currentConversation) {
          setCurrentConversation(data.conversation_id);
          loadConversations(); // Reload conversations to show new one
        }
        
        // Add Conchi's response
        const conchiMessage = {
          id: data.conversation_id + Date.now(),
          sender: 'conchi',
          message: data.message,
          timestamp: data.timestamp,
        };
        
        setMessages(prev => [...prev, conchiMessage]);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };

  const startNewConversation = () => {
    setCurrentConversation(null);
    setMessages([]);
  };

  const formatTime = (timestamp) => {
    const messageDate = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDay = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
    
    // Check if message is from today
    if (messageDay.getTime() === today.getTime()) {
      // Just show time for today's messages
      return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      // Show date and time for older messages
      return messageDate.toLocaleString([], { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const getWelcomeMessage = () => {
    if (user?.language === 'spanish') {
      return `¡Hola ${user.name}! 💖 Soy Conchi, tu compañera empática de IA. Estoy aquí para apoyo emocional y crecimiento personal, no como terapia profesional. ¿Cómo te sientes hoy?`;
    } else {
      return `Hi ${user.name}! 💖 I'm Conchi, your empathetic AI companion. I'm here for emotional support and personal growth, not as professional therapy. How are you feeling today?`;
    }
  };

  const getText = (key) => {
    const texts = {
      english: {
        title: 'Conchi - AI Companion',
        subtitle: 'Your AI guide for self-love and emotional support',
        newChat: 'New Chat',
        placeholder: 'Type your message...',
        typing: 'AI is typing...',
        noConversations: 'No conversations yet',
        startChatting: 'Start chatting to see your conversations here.',
      },
      spanish: {
        title: 'Conchi - Compañera de IA',
        subtitle: 'Tu guía de IA para el amor propio y apoyo emocional',
        newChat: 'Nueva Conversación',
        placeholder: 'Escribe tu mensaje...',
        typing: 'IA está escribiendo...',
        noConversations: 'No hay conversaciones aún',
        startChatting: 'Empieza a conversar para ver tus conversaciones aquí.',
      }
    };
    const language = user?.language === 'spanish' ? 'spanish' : 'english';
    return texts[language]?.[key] || texts.english[key] || '';
  };

  return (
    <>
      {/* ChatGPT-Style Full Screen Layout (Mobile & Desktop) */}
      <div className="fixed inset-x-0 bottom-0 flex flex-col bg-white pb-20" style={{top: '80px'}}>
        {/* Mobile Sidebar Overlay */}
        {showMobileSidebar && (
          <div className="fixed inset-0 z-[100] bg-black/50" onClick={() => setShowMobileSidebar(false)}>
            <div className="w-64 h-full bg-white shadow-xl p-4 pt-24 flex flex-col" onClick={(e) => e.stopPropagation()}>
              {/* Sidebar Header */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800">Chats</h3>
                <button onClick={() => setShowMobileSidebar(false)}>
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              
              {/* New Chat Button */}
              <Button
                onClick={() => {
                  startNewConversation();
                  setShowMobileSidebar(false);
                }}
                size="sm"
                className="bg-rose-500 hover:bg-pink-600 w-full mb-4"
              >
                {getText('newChat')}
              </Button>
              
              {/* Conversations List */}
              <div className="flex-1 overflow-y-auto space-y-2">
                {conversations.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{getText('noConversations')}</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => {
                        loadConversation(conv.id);
                        setShowMobileSidebar(false);
                      }}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        currentConversation === conv.id
                          ? 'bg-rose-100 border border-rose-300'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <h4 className="font-medium text-sm text-gray-800 truncate">{conv.title || conv.last_message || 'New Conversation'}</h4>
                      <p className="text-xs text-gray-500 truncate mt-1">{conv.last_message || 'Start chatting...'}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Mobile Chat Header - Fixed */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center z-20">
          <button onClick={() => setShowMobileSidebar(true)} className="mr-3">
            <Menu className="w-6 h-6 text-gray-600" />
          </button>
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gradient-to-r from-rose-500 to-rose-500 rounded-full flex items-center justify-center mr-2">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">{getText('title')}</h2>
            </div>
          </div>
        </div>
        
        {/* Mobile Messages - Full Height */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-gradient-to-br from-rose-50 via-white to-purple-50">
          {messages.length === 0 && !currentConversation && (
            <div className="text-center mt-8">
              <div className="w-16 h-16 bg-gradient-to-r from-rose-400 to-rose-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                {user?.language === 'spanish' ? '¡Hola!' : 'Welcome!'}
              </h3>
              <p className="text-gray-600 px-4 text-sm">
                {getWelcomeMessage()}
              </p>
            </div>
          )}

          <div className="space-y-4">
            {messages.map((message, index) => (
              <div
                key={message.id || index}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%]`}>
                  {message.sender === 'conchi' && (
                    <div className="flex items-center mb-1">
                      <div className="w-6 h-6 bg-gradient-to-r from-rose-500 to-rose-500 rounded-full flex items-center justify-center mr-2">
                        <Bot className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-xs text-gray-500 font-medium">Conchi</span>
                    </div>
                  )}
                  <div
                    className={`px-4 py-3 rounded-2xl ${
                      message.sender === 'user'
                        ? 'bg-rose-500 text-white rounded-br-none'
                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                    }`}
                  >
                    <p className="whitespace-pre-wrap text-sm">{message.message}</p>
                    <p className={`text-xs mt-1 ${
                      message.sender === 'user' ? 'text-rose-200' : 'text-gray-500'
                    }`}>
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="flex items-center space-x-2 bg-gray-100 px-4 py-3 rounded-2xl rounded-bl-none">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div ref={messagesEndRef} />
        </div>
        
        {/* Mobile Message Input - Fixed Above Bottom Nav */}
        <div className="flex-shrink-0 border-t border-gray-200 px-3 pt-2 pb-1 bg-white z-20">
          <form onSubmit={sendMessage} className="flex space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={user?.language === 'spanish' ? 'Escribe un mensaje...' : 'Type a message...'}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:border-rose-500 text-sm"
              disabled={isLoading}
            />
            <Button
              type="submit"
              disabled={isLoading || !newMessage.trim()}
              className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 rounded-full p-2"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      </div>

      {/* Desktop layout removed - now uses same ChatGPT-style layout as mobile */}
      <div className="hidden">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-r from-rose-500 to-rose-500 rounded-full flex items-center justify-center mr-4">
                <Bot className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{getText('title')}</h1>
                <p className="text-gray-600">{getText('subtitle')}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* Conversations Sidebar */}
            <div className="lg:col-span-1">
              <Card className="p-4 bg-white/70 backdrop-blur-sm border-rose-200 h-[600px] flex flex-col">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800">Conversations</h3>
                <Button
                  onClick={startNewConversation}
                  size="sm"
                  className="bg-rose-500 hover:bg-pink-600 text-xs"
                >
                  {getText('newChat')}
                </Button>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-2">
                {conversations.length === 0 ? (
                  <div className="text-center text-gray-500 mt-8">
                    <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">{getText('noConversations')}</p>
                    <p className="text-xs mt-1">{getText('startChatting')}</p>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <div
                      key={conv.id}
                      onClick={() => loadConversation(conv.id)}
                      className={`p-3 rounded-lg cursor-pointer transition-colors ${
                        currentConversation === conv.id
                          ? 'bg-rose-100 border-rose-300'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <h4 className="font-medium text-sm text-gray-800 truncate">{conv.title || conv.last_message || 'New Conversation'}</h4>
                      <p className="text-xs text-gray-500 truncate mt-1">{conv.last_message || 'Start chatting...'}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(conv.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>

          {/* Chat Area - Full Screen on Mobile */}
          <div className="lg:col-span-3">
            <Card className="p-0 bg-white/70 backdrop-blur-sm border-rose-200 h-[calc(100vh-140px)] md:h-[600px] flex flex-col">
              {/* Messages Area */}
              <div className="flex-1 p-4 overflow-y-auto">
                {messages.length === 0 && !currentConversation && (
                  <div className="text-center mt-8">
                    <div className="w-16 h-16 bg-gradient-to-r from-rose-400 to-rose-400 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      {user?.language === 'spanish' ? '¡Hola!' : 'Welcome!'}
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto text-sm">
                      {getWelcomeMessage()}
                    </p>
                  </div>
                )}

                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div
                      key={message.id || index}
                      className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${message.sender === 'user' ? 'order-1' : 'order-2'}`}>
                        {message.sender === 'conchi' && (
                          <div className="flex items-center mb-1">
                            <div className="w-6 h-6 bg-gradient-to-r from-rose-500 to-rose-500 rounded-full flex items-center justify-center mr-2">
                              <Bot className="w-3 h-3 text-white" />
                            </div>
                            <span className="text-xs text-gray-500 font-medium">AI Assistant</span>
                          </div>
                        )}
                        <div
                          className={`px-4 py-3 rounded-lg ${
                            message.sender === 'user'
                              ? 'bg-rose-500 text-white'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <p className="whitespace-pre-wrap">{message.message}</p>
                          <p className={`text-xs mt-1 ${
                            message.sender === 'user' ? 'text-rose-200' : 'text-gray-500'
                          }`}>
                            {formatTime(message.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {isTyping && (
                    <div className="flex justify-start">
                      <div className="flex items-center space-x-2 bg-gray-100 px-4 py-3 rounded-lg">
                        <div className="w-6 h-6 bg-gradient-to-r from-rose-500 to-rose-500 rounded-full flex items-center justify-center">
                          <Bot className="w-3 h-3 text-white" />
                        </div>
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                        <span className="text-xs text-gray-500">{getText('typing')}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="border-t border-gray-200 p-4">
                <form onSubmit={sendMessage} className="flex space-x-2">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={getText('placeholder')}
                    disabled={isLoading}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500 disabled:bg-gray-100"
                  />
                  <Button
                    type="submit"
                    disabled={isLoading || !newMessage.trim()}
                    className="bg-rose-500 hover:bg-pink-600"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </form>
              </div>
            </Card>
          </div>
          {/* End Chat Area */}

          </div>
          {/* End Grid */}

          {/* Gentle Disclaimer */}
          <div className="text-center mt-4">
            <p className="text-xs text-gray-500 font-sans italic">
              💕 Conchi provides emotional support and growth guidance • For professional therapy, please consult a licensed therapist
            </p>
          </div>
        </div>
        {/* End max-w-7xl container */}
      </div>
      {/* End Desktop Layout */}
    </>
  );
};

export default Companion;